import React, { useState, useEffect, useRef } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner, Input } from '../ui.jsx';

// `bottomOffset` lets callers nudge the bubble up so it clears a fixed bottom
// nav (the Tech Portal has one). Default 20 = normal owner-shell positioning.
export function HelpWidget({ currentPage, bottomOffset = 20, leftOffset = null }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('chat');
  const [faqs, setFaqs] = useState([]);
  const [messages, setMessages] = useState([
    { role:'ai', text:"Hi! I'm your TradeOps help assistant. Ask me anything about how to use the platform, or browse the FAQ tab." }
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (open && faqs.length===0) api('/api/help/faq').then(setFaqs).catch(()=>{}); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  const ask = async () => {
    if (!input.trim()) return;
    const q = input.trim();
    setInput('');
    setMessages(m => [...m, { role:'user', text: q }]);
    setBusy(true);
    try {
      const r = await api('/api/help/ask', { method:'POST', body:{ question: q, currentPage }});
      setMessages(m => [...m, { role:'ai', text: r.answer || r.error || '(no answer)' }]);
    } catch(e) {
      setMessages(m => [...m, { role:'ai', text: 'Sorry — '+e.message }]);
    } finally { setBusy(false); }
  };

  // Tech portal places the FAB at bottom-left (Quick Create FAB lives at
  // bottom-right). On owner shell, both sit bottom-right but the QuickCreate
  // FAB is slightly higher so they stagger nicely.
  const sidePos = leftOffset !== null ? { left: leftOffset } : { right: 20 };

  if (!open) {
    return <div onClick={()=>setOpen(true)} style={{
      position:'fixed', bottom: bottomOffset, ...sidePos, zIndex:9000,
      width:54, height:54, borderRadius:'50%',
      background:`linear-gradient(135deg,${G.purple},${G.cyan})`,
      color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:24, cursor:'pointer', boxShadow:'0 4px 14px rgba(139,92,246,.45)',
      transition:'transform .12s',
    }} title="Help">💬</div>;
  }

  return <div style={{
    position:'fixed', bottom: bottomOffset, ...sidePos, zIndex:9000,
    width:'min(380px, calc(100vw - 32px))', maxHeight:'72vh', display:'flex', flexDirection:'column',
    background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:14,
    boxShadow:'0 10px 40px rgba(0,0,0,.45)',
  }}>
    <div style={{
      padding:'12px 16px', borderBottom:`1px solid ${G.border}`,
      background:`linear-gradient(135deg,${G.purpleDim},transparent)`,
      borderRadius:'14px 14px 0 0',
      display:'flex', alignItems:'center', justifyContent:'space-between',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ fontSize:18 }}>💬</span>
        <strong style={{ fontSize:14 }}>TradeOps Help</strong>
      </div>
      <span onClick={()=>setOpen(false)} style={{ cursor:'pointer', color:G.textMuted, fontSize:18 }}>×</span>
    </div>

    <div style={{ display:'flex', borderBottom:`1px solid ${G.border}` }}>
      <div onClick={()=>setTab('chat')} style={{ flex:1, textAlign:'center', padding:'8px 0', cursor:'pointer', borderBottom: tab==='chat'?`2px solid ${G.purple}`:'2px solid transparent', color: tab==='chat'?G.purple:G.textMuted, fontSize:13, fontWeight:600 }}>Ask AI</div>
      <div onClick={()=>setTab('faq')} style={{ flex:1, textAlign:'center', padding:'8px 0', cursor:'pointer', borderBottom: tab==='faq'?`2px solid ${G.purple}`:'2px solid transparent', color: tab==='faq'?G.purple:G.textMuted, fontSize:13, fontWeight:600 }}>FAQ</div>
    </div>

    {tab === 'chat' ? <>
      <div style={{ flex:1, overflow:'auto', padding:14 }}>
        {messages.map((m,i) => (
          <div key={i} style={{ display:'flex', justifyContent: m.role==='user'?'flex-end':'flex-start', marginBottom:8 }}>
            <div style={{
              maxWidth:'85%', padding:'8px 12px', borderRadius:10,
              background: m.role==='user' ? G.purple+'33' : G.input,
              border: `1px solid ${m.role==='user' ? G.purpleBorder : G.cardBorder}`,
              fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap',
            }}>{m.text}</div>
          </div>
        ))}
        {busy && <div style={{ display:'flex', justifyContent:'flex-start', marginBottom:8 }}>
          <div style={{ padding:'8px 12px', borderRadius:10, background:G.input, fontSize:13 }}><Spinner sz={14}/></div>
        </div>}
        <div ref={endRef}/>
      </div>
      <div style={{ padding:10, borderTop:`1px solid ${G.border}`, display:'flex', gap:6 }}>
        <Input value={input} onChange={e=>setInput(e.target.value)} placeholder="Ask anything..." onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey) { e.preventDefault(); ask(); }}}/>
        <Btn v="primary" sz="sm" onClick={ask} disabled={busy || !input.trim()}>Send</Btn>
      </div>
    </> : <div style={{ flex:1, overflow:'auto', padding:10 }}>
      {faqs.map((f,i) => <FaqItem key={i} q={f.q} a={f.a}/>)}
    </div>}
  </div>;
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return <div style={{ ...styles.card, padding:10, marginBottom:6, cursor:'pointer' }} onClick={()=>setOpen(!open)}>
    <div style={{ fontSize:13, fontWeight:600, display:'flex', justifyContent:'space-between', gap:8 }}>
      <span>{q}</span>
      <span style={{ color:G.textMuted }}>{open?'−':'+'}</span>
    </div>
    {open && <div style={{ fontSize:12, color:G.textMuted, marginTop:6, lineHeight:1.5 }}>{a}</div>}
  </div>;
}
